
import React, { useState, useEffect, useRef } from "react";
import CompanyLogo from "../components/CompanyLogo";
import AuthFooter from "../components/AuthFooter";
import { useDomain } from "../contexts/DomainContext";
import { getDisplayCompanyName } from "../utils/branding";
import { ssoImpersonate } from "../api/ssoAuth";
import { getRedirectUri, redirectToApp, isRedirectHostnameTrusted } from "../utils/redirectHelper";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./Login.css";

const Impersonate = () => {
  const { domainConfig, domainLoading, appConfig, appName } = useDomain() || {};
  const [impersonationData, setImpersonationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasInitialized = useRef(false);

  const companyName = getDisplayCompanyName(
    domainConfig?.companyName || appName,
    "CRM"
  );

  useEffect(() => {
    // Wait for the domain lookup (DomainContext) to finish so partner
    // domains on their own custom domain can be recognized as trusted.
    if (domainLoading) return;

    // Prevent double execution in React Strict Mode
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const initializeImpersonation = async () => {
      try {
        // Get redirect URI once during initialization
        const redirectUri = getRedirectUri();

        if (redirectUri && !isRedirectHostnameTrusted(redirectUri, domainConfig)) {
          console.error("Impersonation blocked - untrusted redirect_uri hostname");
          setError("This redirect destination is not a recognized app or partner domain.");
          toast.error("Untrusted redirect destination");
          setLoading(false);
          return;
        }

        // Clear session storage and local storage
        localStorage.removeItem("kit19_sso_token");
        localStorage.removeItem("kit19_user_data");
        localStorage.removeItem("kit19_device_id");
        sessionStorage.clear();

        // Parse URL parameters
        const params = new URLSearchParams(window.location.search);
        const configStr = params.get("config");
        let decodedData = null;
        let fullConfig = null;
        
        if (configStr) {
          try {
            fullConfig = JSON.parse(configStr);
            if (fullConfig.Impersonation) {
              // Try to decode if it's base64 encoded
              try {
                decodedData = JSON.parse(atob(fullConfig.Impersonation));
                setImpersonationData(decodedData);
              } catch (e) {
                // If not base64, use as is
                decodedData = fullConfig.Impersonation;
                setImpersonationData(decodedData);
              }

              // Call impersonate API with the config
              if (fullConfig.apiBase) {
                try {
                  // The Impersonation payload sent by the apps (e.g. CRM) carries no token,
                  // so fall back to the config's tokenKey as before.
                  const authToken =
                    (decodedData && (decodedData.token || decodedData.accessToken)) ||
                    fullConfig.tokenKey ||
                    "kit19_sso_token";

                  const _payload = {
                    loginMode: fullConfig.loginMode,
                    impersonationData: decodedData,
                    redirectUrl: fullConfig.redirectUrl,
                    appName: fullConfig.appName
                  };

                  

                  const response = await ssoImpersonate(authToken, _payload);

                  // Parse raw API response same as ssoLogin does internally
                  const resData = response.data;
                  if (resData?.status === 1) {
                    const userInfo = resData.details?.userInfo;
                    const tokenInfo = resData.details?.tokenInfo;
                    const domainInfo = resData.details?.domainInfo;
                    const meta = resData.details?.meta;

                    const token = tokenInfo?.accessToken;
                    const userData = {
                      userId: userInfo?.userId,
                      User_ID: userInfo?.userId,
                      parentId: userInfo?.parentId,
                      ParentID: userInfo?.parentId,
                      firstName: meta?.userDetail?.fName || meta?.teamChat?.FName || '',
                      FName: meta?.userDetail?.fName || meta?.teamChat?.FName || '',
                      lastName: meta?.userDetail?.lName || meta?.teamChat?.LName || '',
                      LName: meta?.userDetail?.lName || meta?.teamChat?.LName || '',
                      email: meta?.userDetail?.email || meta?.teamChat?.EMail || '',
                      EMail: meta?.userDetail?.email || meta?.teamChat?.EMail || '',
                      domain: domainInfo?.domainName || '',
                      Domain: domainInfo?.domainName || '',
                      displayName: userInfo?.displayName || '',
                      DisplayName: userInfo?.displayName || '',
                      username: userInfo?.userLogin || '',
                      LoginName: userInfo?.userLogin || '',
                      tpUserId: userInfo?.tpUserId,
                      appName: userInfo?.appName,
                      loginMode: userInfo?.loginMode,
                      impersonationData: decodedData
                    };

                    if (token) {
                      // Store token based on config or default — same as Login.js
                      const tokenKey = fullConfig?.tokenKey || "kit19_sso_token";
                      localStorage.setItem(tokenKey, token);
                      localStorage.setItem("kit19_sso_token", token);
                      localStorage.setItem("kit19_user_data", JSON.stringify(userData));

                      // Store configuration for logout redirect
                      if (fullConfig) {
                        localStorage.setItem("kit19_sso_config", JSON.stringify(fullConfig));
                      }

                      toast.success("Impersonation successful! Redirecting...");

                      // Redirect back to the app — same as Login.js
                      if (redirectUri) {
                        console.log("Redirecting to app in 1 second...");
                        const deviceId = localStorage.getItem("kit19_device_id") || "";
                        setTimeout(() => {
                          redirectToApp(redirectUri, token, userData, fullConfig, deviceId);
                        }, 1000);
                      } else {
                        console.log("No redirect URI - staying on SSO page");
                        toast.info("Impersonation successful! Please return to your application.");
                      }
                    } else {
                      throw new Error("No token received in response");
                    }
                  } else {
                    throw new Error(resData?.message || "Impersonation failed");
                  }
                } catch (apiError) {
                  console.error("Impersonate API call failed:", apiError);
                  setError(apiError.message || "Impersonation failed. Please try again.");
                  toast.error(apiError.message || "Impersonation failed");
                }
              }
            }
          } catch (e) {
            console.warn("Could not parse config:", e);
            setError("Invalid configuration");
            toast.error("Invalid configuration");
          }
        }
        setLoading(false);
      } catch (err) {
        console.error("Error initializing impersonation:", err);
        setError(err.message);
        toast.error(err.message || "An error occurred");
        setLoading(false);
      }
    };

    initializeImpersonation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainLoading]);

  if (loading) {
    return (
      <div className="sso-login-container">
        <ToastContainer position="top-right" autoClose={3000} />
        <div className="login-card">
          <div style={{ textAlign: "center", padding: "40px" }}>
            <div style={{ fontSize: "48px", marginBottom: "20px" }}>⏳</div>
            <h2>Setting up Impersonation...</h2>
            <p style={{ color: "#666", marginTop: "10px" }}>
              Please wait while we prepare your session
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sso-login-container">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="login-card">
        <div className="login-header">
          <CompanyLogo
            appConfig={appConfig}
            domainConfig={domainConfig}
            appName={appName}
          />
          <h2 style={{ color: "#374151", fontSize: "20px", marginTop: "16px" }}>
            Impersonating User
          </h2>
        </div>

        <div style={{ textAlign: "center", marginBottom: "24px" }}>
          <p className="subtitle">
            You are currently impersonating a user in <strong style={{ color: "#667eea" }}>{companyName}</strong>.
          </p>
          
          {error && (
            <div style={{
              background: "#fee2e2",
              border: "1px solid #ef4444",
              borderLeft: "4px solid #ef4444",
              padding: "16px",
              borderRadius: "8px",
              marginBottom: "16px",
              textAlign: "left",
              color: "#991b1b",
              fontSize: "13px"
            }}>
              <strong>Error:</strong> {error}
            </div>
          )}
          
          <p style={{ color: "#6b7280", fontSize: "14px", lineHeight: "1.6", marginBottom: "16px" }}>
            You have access to this user's account and can perform actions on their behalf.
          </p>
          
          <div style={{
            background: "#fff3cd",
            border: "1px solid #ffc107",
            borderLeft: "4px solid #ffc107",
            padding: "16px",
            borderRadius: "8px",
            textAlign: "left",
            color: "#856404",
            fontSize: "13px",
            lineHeight: "1.6"
          }}>
            <strong style={{ display: "block", marginBottom: "6px", color: "#ff6b6b" }}>
              Important:
            </strong>
            All actions taken while impersonating are logged and monitored for security and compliance purposes.
          </div>
        </div>

        <AuthFooter domainConfig={domainConfig} />
      </div>
    </div>
  );
};

export default Impersonate;